import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Phone, Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    package: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simulate form submission
    const whatsappMessage = `Assalamualaikum, saya ingin konsultasi:%0A%0ANama: ${formData.name}%0ANo HP: ${formData.phone}%0AEmail: ${formData.email}%0APaket: ${formData.package}%0APesan: ${formData.message}`;
    
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`, '_blank');
    
    toast.success('Terima kasih! Anda akan diarahkan ke WhatsApp');
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      package: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-[#8B0000]/10 text-[#8B0000] border-[#8B0000]/20">
            Hubungi Kami
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Siap Membantu <span className="text-[#8B0000]">Perjalanan Ibadah</span> Anda
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tim kami siap melayani dan menjawab semua pertanyaan Anda
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Informasi Kontak</h3>
              <p className="text-gray-600 mb-8">
                Hubungi kami melalui salah satu channel di bawah ini. Tim kami siap melayani Anda dengan sepenuh hati.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <Card className="border-2 border-gray-100 hover:border-[#F5C842] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#8B0000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#8B0000]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Telepon / WhatsApp</h4>
                      <p className="text-gray-600 mb-2">{companyInfo.contactPerson}</p>
                      <a 
                        href={`tel:${companyInfo.phone}`}
                        className="text-[#8B0000] font-medium hover:underline"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-[#F5C842] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#8B0000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#8B0000]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-[#8B0000] font-medium hover:underline"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-[#F5C842] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#8B0000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#8B0000]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Alamat Kantor</h4>
                      <p className="text-gray-600">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick WhatsApp Button */}
            <Card className="bg-gradient-to-r from-green-500 to-green-600 border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between text-white">
                  <div>
                    <h4 className="font-bold text-lg mb-1">Chat Langsung via WhatsApp</h4>
                    <p className="text-sm text-white/90">Respon cepat dalam hitungan menit</p>
                  </div>
                  <a href={`https://wa.me/${companyInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-white text-green-600 hover:bg-gray-100">
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Chat Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="border-2 border-gray-100 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Form Konsultasi</h3>
              <p className="text-gray-600 mb-6">Isi form di bawah ini dan kami akan segera menghubungi Anda</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Nama Lengkap *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Masukkan nama lengkap"
                    required
                    className="border-gray-200 focus:border-[#8B0000]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Nomor WhatsApp *
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="08xxxxxxxxxx"
                    required
                    className="border-gray-200 focus:border-[#8B0000]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Email
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className="border-gray-200 focus:border-[#8B0000]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Paket yang Diminati
                  </label>
                  <Input
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    placeholder="Contoh: Umroh Plus Mesir"
                    className="border-gray-200 focus:border-[#8B0000]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Pesan / Pertanyaan
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tuliskan pertanyaan atau pesan Anda..."
                    rows={4}
                    className="border-gray-200 focus:border-[#8B0000] resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#8B0000] hover:bg-[#A52A2A] text-white"
                  size="lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Kirim Pesan
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
